import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Smartphone,
  CalendarDays,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MESES } from '@/components/constants/meses';
import { GLASS_SURFACE } from '@/components/ui/glass';

interface Props {
  mes: number;
  anio: number;
  setMes: React.Dispatch<React.SetStateAction<number>>;
  setAnio: React.Dispatch<React.SetStateAction<number>>;
  showPwaButton?: boolean;
  onPwaInstall?: () => void;
  isIOS?: boolean;
}

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 320, damping: 30, mass: 0.7 },
  },
};

const DashboardHeader = ({
  mes,
  anio,
  setMes,
  setAnio,
  showPwaButton,
  onPwaInstall,
  isIOS,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const nombreMes =
    MESES[mes - 1].charAt(0).toUpperCase() + MESES[mes - 1].slice(1);

  return (
    <>
      {/* Cabecera. El pr-32 reserva sitio para los botones fijos globales
          (ayuda + notificaciones), que viven arriba a la derecha: sin él,
          el selector de mes queda debajo de ellos. Debajo del selector va
          un texto pequeño, solo visible en pc. */}
      <motion.div
        variants={itemVariants}
        className="flex items-center gap-3 w-full flex-wrap pr-32"
      >
        <div className="flex flex-col gap-1">
          <div className="relative tour-month-selector" ref={popRef}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => setOpen((o) => !o)}
              className={`flex items-center gap-2 cursor-pointer rounded-2xl px-3 py-2 ${GLASS_SURFACE} border border-white/40 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors`}
            >
              <CalendarDays className="w-4 h-4" />
              <span className="text-sm font-medium whitespace-nowrap">
                {nombreMes.slice(0, 3)} {anio}
              </span>
            </motion.button>

            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                  className={`absolute left-0 mt-2 w-72 rounded-2xl p-4 z-50 ${GLASS_SURFACE} border border-white/40 dark:border-white/10 shadow-xl`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => setAnio((a) => a - 1)}
                      className="p-1.5 rounded-lg hover:bg-white/60 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {anio}
                    </span>
                    <button
                      onClick={() => setAnio((a) => a + 1)}
                      className="p-1.5 rounded-lg hover:bg-white/60 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {MESES.map((nombre, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setMes(idx + 1);
                          setOpen(false);
                        }}
                        className={`text-xs font-medium py-2 rounded-xl transition-colors ${
                          mes === idx + 1
                            ? 'bg-[#103254] text-white shadow-md shadow-[#103254]/30'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-white/10'
                        }`}
                      >
                        {nombre.charAt(0).toUpperCase() + nombre.slice(1, 3)}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500 pl-1">
            Resumen de tu actividad
          </span>
        </div>
      </motion.div>

      {/* Aviso de instalación — solo esto, nada más */}
      {showPwaButton && (
        <motion.div
          variants={itemVariants}
          className="relative isolate overflow-hidden p-4 rounded-2xl bg-yellow-50/90 text-blue-800 border border-yellow-200/80 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-700/40 shadow-[0_4px_16px_-8px_rgba(202,138,4,0.3)] tour-banner"
        >
          <div className="flex items-start gap-3">
            {isIOS ? (
              <p className="text-xs text-blue-700 dark:text-amber-300 flex items-center gap-1.5">
                <Smartphone className="w-3.5 h-3.5 shrink-0" />
                {t('dashboardHeader.iosInstallHint')}
              </p>
            ) : (
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={onPwaInstall}
                className="relative isolate overflow-hidden inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#103254] hover:bg-[#0d2742] px-3.5 py-2 rounded-xl shadow-[0_4px_14px_-4px_rgba(16,50,84,0.5),inset_0_1px_0_rgba(255,255,255,0.14)] transition-all duration-200 active:scale-[0.98]"
              >
                <Download className="w-3.5 h-3.5" />
                {t('dashboardHeader.installApp')}
              </motion.button>
            )}
          </div>
        </motion.div>
      )}
    </>
  );
};

export default DashboardHeader;